import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag, KineticLine } from "@/components/Reveal";
import Ticker from "@/components/Ticker";
import { products, CONTACT } from "@/data/site";

const quoteLink = (name) =>
  `${CONTACT.whatsapp}?text=${encodeURIComponent(`Hi Fluton, I'd like a quote for ${name}.`)}`;

export default function Products() {
  return (
    <div data-testid="products-page">
      {/* PAGE HERO */}
      <section className="relative pt-40 pb-24 bg-navy noise overflow-hidden" data-testid="products-hero">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="font-tech text-xs uppercase tracking-[0.3em] text-copper mb-6"
          >
            Our Products
          </motion.p>
          <h1 className="font-display font-black uppercase text-white tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl" data-testid="products-headline">
            <KineticLine text="Every grade." delay={0.15} />
            <KineticLine text="Verified. Shipped." delay={0.3} className="text-copper" />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8 max-w-xl text-gray-300 leading-relaxed"
          >
            ISRI-graded recyclable metals, inspected lot-by-lot and container-loaded at our Dubai
            hub. Tap any product to get a quote on WhatsApp within one business day.
          </motion.p>
        </div>
      </section>

      <Ticker />

      {/* BENTO GRID */}
      <section className="py-24 md:py-32" data-testid="products-grid-section">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTag no="01" label="The Export Line" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8" data-testid="products-grid">
            {products.map((p, i) => (
              <Reveal key={p.name} delay={(i % 3) * 0.08} className={i === 0 || i === 5 ? "md:col-span-2" : ""}>
                <article
                  className="group relative h-full min-h-[320px] overflow-hidden bg-ink flex"
                  data-testid={`product-card-${i}`}
                >
                  <img
                    src={p.img}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/25 to-transparent" />
                  <div className="relative w-full flex flex-col justify-end p-8">
                    <p className="font-tech text-[11px] uppercase tracking-[0.2em] text-copper">{p.spec}</p>
                    <h2 className="font-display font-bold text-white text-2xl tracking-tight mt-2">{p.name}</h2>
                    <p className="mt-3 text-sm text-gray-300 leading-relaxed max-w-md opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      {p.blurb}
                    </p>
                    <a
                      href={quoteLink(p.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`product-quote-${i}`}
                      className="mt-5 inline-flex items-center gap-2 w-fit border border-white/30 text-white font-tech text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 hover:border-copper hover:text-copper transition-colors duration-300"
                    >
                      Get Quote <ArrowUpRight size={13} />
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPEC NOTE */}
      <section className="bg-ink noise py-20" data-testid="products-note-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <Reveal>
            <p className="font-tech text-xs uppercase tracking-[0.25em] text-copper mb-4">Custom Grades</p>
            <h2 className="font-display font-black text-white tracking-tighter text-3xl md:text-4xl max-w-xl leading-tight">
              Need a grade you don't see listed?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={quoteLink("a custom grade")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="products-custom-quote"
              className="group inline-flex items-center gap-3 bg-copper text-white font-tech text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-white hover:text-navy transition-colors duration-300 whitespace-nowrap"
            >
              Ask Our Trading Desk
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
